import { Injectable, OnDestroy } from '@angular/core';

import { IPositionChange, IVector3 } from '@simlab/simlab-facility-management/common';
import { findNearestCollision } from '@simlab/simlab-facility-management/scene-object';
import {
  BehaviorSubject,
  filter,
  firstValueFrom,
  Observable,
  take,
} from 'rxjs';
import { Box3, Vector3 } from 'three';
import { IDisposable, MpSdk } from '../../../assets/bundle/sdk';
import { MatterportServiceBase } from '../base/matterport-base';
import {
  CustomScene,
  customSceneType,
  makeCustomSceneRenderer,
} from '../base/scene-hdr';
import { ISceneState } from '../models/scene-state.interface';
import { MatterportManagerService } from './matterport-manager.service';
@Injectable()
export class MatterportSceneStateAccessService
  extends MatterportServiceBase
  implements OnDestroy, ISceneState {
  private _currentSweepSubscription: MpSdk.ISubscription | undefined;
  private _availableSweepsSubscription: MpSdk.ISubscription | undefined;
  private _currentPoseSubscription: MpSdk.ISubscription | undefined;
  private _sceneComponent: BehaviorSubject<CustomScene | undefined> =
    new BehaviorSubject<CustomScene | undefined>(undefined);
  private _nodeRef!: MpSdk.Scene.INode;
  private readonly _currentSweep: BehaviorSubject<MpSdk.Sweep.ObservableSweepData | null> =
    new BehaviorSubject<MpSdk.Sweep.ObservableSweepData | null>(null);
  private readonly _currentPose: BehaviorSubject<IPositionChange | null> =
    new BehaviorSubject<IPositionChange | null>(null);
  private readonly _availableSweeps: BehaviorSubject<
    MpSdk.Sweep.ObservableSweepData[] | null
  > = new BehaviorSubject<MpSdk.Sweep.ObservableSweepData[] | null>(null);
  readonly sweepChange$ = this._currentSweep.asObservable();
  readonly positionChange$ = this._currentPose.asObservable();
  readonly availableSweeps$ = this._availableSweeps.asObservable();
  private _component: IDisposable | null | undefined;
  private _scene: THREE.Scene | undefined;
  public get scene(): THREE.Scene | undefined {
    return this._scene;
  }
  private set scene(value: THREE.Scene | undefined) {
    this._scene = value;
  }
  private _camera: THREE.Camera | undefined;
  public get camera(): THREE.Camera | undefined {
    return this._camera;
  }
  private set camera(value: THREE.Camera | undefined) {
    this._camera = value;
  }

  constructor(private readonly matterportManager: MatterportManagerService) {
    super(matterportManager);
  }
  findNearestCollision = (dest: IVector3, origin?: IVector3): IVector3 | null => {
    const destV = this.transformConverter.toMatterportPosition(new Vector3(dest.x, dest.y, dest.z))
    const originV = origin ? new Vector3(origin.x, origin.y, origin.z) : this.camera!.parent!.position;
    const pos = findNearestCollision(originV, destV, this.scene!, this.camera!)
    const backV = new Vector3(pos?.x, pos?.y, pos?.z).clone().sub(originV).normalize()
    return this.transformConverter.to3dPosition(new Vector3(pos?.x, pos?.y, pos?.z).sub(backV.multiplyScalar(0.1)));
  };

  override ngOnDestroy(): void {
    this._clear();
    this._nodeRef?.stop();
    this._sceneComponent.next(undefined);

    // console.log('MatterportSceneStateAccessService - destory');
    super.ngOnDestroy();
  }
  protected async _init() {
    this._clear();
    this._currentSweepSubscription = this.sdk.Sweep.current.subscribe(
      (currentSweep: MpSdk.Sweep.ObservableSweepData) => {
        this._currentSweep.next({
          ...currentSweep,
          id: currentSweep.uuid,
        });
      }
    );

    this._currentPoseSubscription = this.sdk.Camera.pose.subscribe(
      (currentPose: MpSdk.Camera.Pose) => {
        this._currentPose.next({
          ...currentPose,

          rotation: {
            ...currentPose.rotation,
            z: 0
          }
        });
      }
    );

    this._availableSweepsSubscription = this.sdk.Sweep.data.subscribe({
      onCollectionUpdated: (
        sweepsCollection: MpSdk.Dictionary<MpSdk.Sweep.ObservableSweepData>
      ) => {
        // console.warn('TO CHECK WITH OLEK WHY UUI?');

        this._availableSweeps.next(
          // Array.from(Object.entries(sweepsCollection), (v) => v[1])
          // TODO change this behavior in backend
          Array.from(Object.entries(sweepsCollection), (v) => ({
            ...v[1],
            id: v[1].uuid,
            _id: v[1].id,
          }))
        );
      },
    });

    this._component = await firstValueFrom(
      this.registerComponents$(customSceneType, makeCustomSceneRenderer)
    ).catch(() => null);
    if (!this._component) return;
    this._registerHDR();
  }

  getAvailableSweeps$(): Observable<MpSdk.Sweep.ObservableSweepData[] | null> {
    return this.availableSweeps$.pipe(
      filter((v) => !!v),
      take(1)
    );
  }

  lastKnownCameraPose(cameraPose: IPositionChange): IPositionChange {
    const transformConverter = this.matterportManager.transformConverter;
    const cameraPoseWithOffset: IPositionChange = {
      ...cameraPose,
      position: transformConverter.to3dPosition(
        new Vector3(
          cameraPose.position.x,
          cameraPose.position.y,
          cameraPose.position.z
        )
      ),
      rotation: transformConverter.to3dRotation(
        new Vector3(cameraPose.rotation.x, cameraPose.rotation.y)
      ),
    };
    return cameraPoseWithOffset;
  }

  private _clear() {
    if (
      this._currentSweepSubscription &&
      Object.keys(this._currentSweepSubscription).length == 0
    )
      this._currentSweepSubscription.cancel();

    if (
      this._currentPoseSubscription &&
      Object.keys(this._currentPoseSubscription).length == 0
    )
      this._currentPoseSubscription.cancel();

    if (this._availableSweepsSubscription)
      this._availableSweepsSubscription.cancel();
  }

  private _registerHDR() {
    this.sdk.Scene.createObjects(1).then(([object]: MpSdk.Scene.IObject[]) => {
      this._nodeRef = object.addNode();
      const component = this._nodeRef.addComponent(
        customSceneType,
        {}
      ) as CustomScene;
      this._nodeRef.start();
      this.scene = component._context?.scene
      this.camera = component._context?.camera
      this._sceneComponent.next(component);
    });
  }

  renderMesh(shadowBox: Box3) {
    firstValueFrom(
      this._sceneComponent
        .asObservable()
        .pipe(filter((customScene) => !!customScene))
    ).then((customScene) => {
      (customScene as CustomScene).renderMesh(shadowBox);
    });
  }
}
